import * as XLSX from 'xlsx';

// Import dynamique de pdfmake pour éviter les erreurs d'initialisation
let pdfMake = null;

// Fonction pour charger pdfmake dynamiquement
const loadPdfMake = async () => {
  if (!pdfMake) {
    const pdfMakeModule = await import('pdfmake/build/pdfmake');
    const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
    pdfMake = pdfMakeModule.default;
    pdfMake.vfs = pdfFontsModule.default.vfs || pdfFontsModule.default.pdfMake?.vfs;
  }
  return pdfMake;
};

// Couleurs du thème SUP2I
const COLORS = {
  primary: '#667eea',
  secondary: '#764ba2',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  dark: '#1f2937',
  light: '#f9fafb'
};

// ========== FONCTIONS GÉNÉRIQUES ==========

/**
 * Exporte des données vers un fichier Excel
 * @param {Array} data - Données à exporter
 * @param {string} filename - Nom du fichier (sans extension)
 * @param {Array} columns - Colonnes à exporter [{ key: 'field', label: 'Titre' }]
 */
export const exportToExcel = (data, filename, columns) => {
  if (!data || data.length === 0) {
    console.warn('Aucune donnée à exporter');
    return;
  }

  // Formater les données pour Excel
  const formattedData = data.map(item => {
    const row = {};
    columns.forEach(col => {
      let value = item[col.key];
      if (value === undefined || value === null) value = '';
      if (value instanceof Date) value = value.toLocaleDateString('fr-FR');
      row[col.label] = value;
    });
    return row;
  });

  // Créer la feuille de calcul
  const ws = XLSX.utils.json_to_sheet(formattedData);
  
  // Ajuster la largeur des colonnes
  const colWidths = columns.map(col => ({
    wch: Math.max(col.label.length, ...formattedData.map(row => String(row[col.label] || '').length)) + 2
  }));
  ws['!cols'] = colWidths;

  // Créer le classeur
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, filename);
  
  // Télécharger
  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`);
};

/**
 * Exporte des données vers un fichier PDF
 * @param {Array} data - Données à exporter
 * @param {string} title - Titre du document
 * @param {Array} columns - Colonnes à exporter [{ key: 'field', label: 'Titre' }]
 * @param {Object} options - Options supplémentaires
 */
export const exportToPDF = async (data, title, columns, options = {}) => {
  if (!data || data.length === 0) {
    console.warn('Aucune donnée à exporter');
    return;
  }

  try {
    const pdf = await loadPdfMake();
    if (!pdf) {
      console.error('Impossible de charger pdfmake');
      // Fallback: ouvrir une nouvelle fenêtre pour impression
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head><title>${title}</title></head>
          <body>
            <h1>${title}</h1>
            <table border="1" cellpadding="5" cellspacing="0">
              <thead>
                <tr>
                  ${columns.map(col => `<th>${col.label}</th>`).join('')}
                 </tr>
              </thead>
              <tbody>
                ${data.map(item => `
                  <tr>
                    ${columns.map(col => `<td>${item[col.key] || '-'}</td>`).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <p>Généré le ${new Date().toLocaleDateString('fr-FR')}</p>
          </body>
        </html>
      `);
      printWindow.print();
      return;
    }

    const currentDate = new Date().toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    const documentDefinition = {
      pageOrientation: data.length > 20 ? 'landscape' : 'portrait',
      pageMargins: [40, 80, 40, 60],
      header: () => ({
        columns: [
          {
            text: 'SUP2I - Gestion Scolarité',
            alignment: 'left',
            fontSize: 8,
            color: '#9ca3af'
          },
          {
            text: `Date: ${currentDate}`,
            alignment: 'right',
            fontSize: 8,
            color: '#9ca3af'
          }
        ],
        margin: [40, 20, 40, 0]
      }),
      content: [
        {
          text: title,
          alignment: 'center',
          fontSize: 18,
          bold: true,
          color: COLORS.dark,
          margin: [0, 0, 0, 10]
        },
        {
          text: `Généré le ${currentDate}`,
          alignment: 'center',
          fontSize: 10,
          color: '#6b7280',
          margin: [0, 0, 0, 20]
        },
        {
          table: {
            headerRows: 1,
            widths: columns.map(() => 'auto'),
            body: [
              // En-tête
              columns.map(col => ({
                text: col.label,
                bold: true,
                fillColor: COLORS.primary,
                color: 'white',
                alignment: 'center',
                fontSize: 10,
                margin: [4, 6, 4, 6]
              })),
              // Données
              ...data.map((item, index) => 
                columns.map(col => {
                  let value = item[col.key];
                  if (value === undefined || value === null) value = '-';
                  if (col.type === 'date' && value) value = new Date(value).toLocaleDateString('fr-FR');
                  if (col.type === 'currency' && typeof value === 'number') value = `${value.toLocaleString()} DH`;
                  
                  return {
                    text: String(value),
                    fontSize: 9,
                    margin: [4, 4, 4, 4],
                    fillColor: index % 2 === 0 ? COLORS.light : 'white'
                  };
                })
              )
            ]
          },
          layout: {
            hLineColor: () => '#e5e7eb',
            vLineColor: () => '#e5e7eb',
            paddingLeft: () => 4,
            paddingRight: () => 4
          }
        }
      ],
      footer: (currentPage, pageCount) => ({
        text: `Page ${currentPage} / ${pageCount}`,
        alignment: 'center',
        fontSize: 8,
        color: '#9ca3af',
        margin: [0, 10, 0, 0]
      }),
      defaultStyle: {
        fontSize: 10
      }
    };

    pdf.createPdf(documentDefinition).download(`${title}_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (error) {
    console.error('Erreur lors de la génération du PDF:', error);
    alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
  }
};

// ========== EXPORTS SPÉCIFIQUES POUR ÉTUDIANTS ==========

export const exportEtudiantsToExcel = (etudiants) => {
  const columns = [
    { key: 'cne', label: 'CNE' },
    { key: 'nom', label: 'Nom' },
    { key: 'prenom', label: 'Prénom' },
    { key: 'email', label: 'Email' },
    { key: 'telephone', label: 'Téléphone' },
    { key: 'filiere', label: 'Filière' },
    { key: 'niveau', label: 'Niveau' },
    { key: 'statut', label: 'Statut' }
  ];

  const data = etudiants.map(e => ({
    cne: e.cne || e.etudiant_cne_masar || '-',
    nom: e.nom || e.etudiant_nom || '-',
    prenom: e.prenom || e.etudiant_prenom || '-',
    email: e.email || e.etudiant_email || '-',
    telephone: e.telephone || e.etudiant_tel || '-',
    filiere: e.filiereInfo?.libelle || e.filiere || e.filiereInfo?.nom || '-',
    niveau: e.niveauInfo?.nom || e.niveau || '-',
    statut: e.statutInscription === 'CONFIRMEE' ? 'Inscrit' : e.statutInscription === 'EN_ATTENTE' ? 'En attente' : 'Non inscrit'
  }));

  exportToExcel(data, 'Liste_Etudiants', columns);
};

export const exportEtudiantsToPDF = async (etudiants) => {
  const columns = [
    { key: 'cne', label: 'CNE' },
    { key: 'nom', label: 'Nom' },
    { key: 'prenom', label: 'Prénom' },
    { key: 'email', label: 'Email' },
    { key: 'filiere', label: 'Filière' },
    { key: 'statut', label: 'Statut' }
  ];

  const data = etudiants.map(e => ({
    cne: e.cne || e.etudiant_cne_masar || '-',
    nom: e.nom || e.etudiant_nom || '-',
    prenom: e.prenom || e.etudiant_prenom || '-',
    email: e.email || e.etudiant_email || '-',
    filiere: e.filiereInfo?.libelle || e.filiere || '-',
    statut: e.statutInscription === 'CONFIRMEE' ? 'Inscrit' : e.statutInscription === 'EN_ATTENTE' ? 'En attente' : 'Non inscrit'
  }));

  await exportToPDF(data, 'Liste_des_Etudiants', columns);
};

// ========== EXPORTS SPÉCIFIQUES POUR ENSEIGNANTS ==========

export const exportEnseignantsToExcel = (enseignants) => {
  const columns = [
    { key: 'matricule', label: 'Matricule' },
    { key: 'nom', label: 'Nom' },
    { key: 'prenom', label: 'Prénom' },
    { key: 'email', label: 'Email' },
    { key: 'telephone', label: 'Téléphone' },
    { key: 'specialite', label: 'Spécialité' },
    { key: 'grade', label: 'Grade' },
    { key: 'statut', label: 'Statut' },
    { key: 'departement', label: 'Département' }
  ];

  const data = enseignants.map(e => ({
    matricule: e.matricule || '-',
    nom: e.nom || '-',
    prenom: e.prenom || '-',
    email: e.email || '-',
    telephone: e.telephone || '-',
    specialite: e.specialite || '-',
    grade: e.grade || '-',
    statut: e.statut || 'Actif',
    departement: e.departement || '-'
  }));

  exportToExcel(data, 'Liste_Enseignants', columns);
};

export const exportEnseignantsToPDF = async (enseignants) => {
  const columns = [
    { key: 'matricule', label: 'Matricule' },
    { key: 'nom', label: 'Nom' },
    { key: 'prenom', label: 'Prénom' },
    { key: 'specialite', label: 'Spécialité' },
    { key: 'grade', label: 'Grade' }
  ];

  const data = enseignants.map(e => ({
    matricule: e.matricule || '-',
    nom: e.nom || '-',
    prenom: e.prenom || '-',
    specialite: e.specialite || '-',
    grade: e.grade || '-'
  }));

  await exportToPDF(data, 'Liste_des_Enseignants', columns);
};

// ========== EXPORT STATISTIQUES DASHBOARD ==========

export const exportDashboardToPDF = async (stats, statsMatiere = [], topEtudiants = []) => {
  const currentDate = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  // Données des cartes KPI
  const kpiData = [
    { indicateur: 'Total Étudiants', valeur: stats.totalEtudiants || 0 },
    { indicateur: 'Total Enseignants', valeur: stats.totalEnseignants || 0 },
    { indicateur: 'Total Cours', valeur: stats.totalCours || 0 },
    { indicateur: 'Total Inscriptions', valeur: stats.totalInscriptions || 0 },
    { indicateur: 'Chiffre d\'Affaires', valeur: `${(stats.totalPaiements || 0).toLocaleString()} DH` },
    { indicateur: 'Moyenne Générale', valeur: `${stats.moyenneGenerale || 0}/20` },
    { indicateur: 'Taux de Réussite', valeur: `${stats.tauxReussiteGlobal || 0}%` }
  ];

  const pdfData = [
    ...kpiData,
    ...(statsMatiere && statsMatiere.length > 0 ? statsMatiere.slice(0, 10).map(m => ({
      indicateur: m._id || 'Matière',
      valeur: m.moyenne?.toFixed(2) || '-'
    })) : [])
  ];

  const columns = [
    { key: 'indicateur', label: 'Indicateur' },
    { key: 'valeur', label: 'Valeur' }
  ];

  await exportToPDF(pdfData, 'Rapport_Statistique', columns);
};

// ========== EXPORT DES PRÉSENCES ==========

// ========== ATTESTATION DE SCOLARITÉ ==========

const getBase64FromUrl = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

export const generateAttestationPDF = async (etudiant, inscription) => {
  try {
    const pdf = await loadPdfMake();
    if (!pdf) {
      alert('Impossible de générer le PDF');
      return;
    }

    const civilité = etudiant.sexe === 'F' ? 'Mme' : 'Mr';
    const nomComplet = `${etudiant.Nom || etudiant.nom || ''} ${etudiant.Prénom || etudiant.prenom || ''}`.trim();
    const dateNaissance = etudiant.date_naissance || '…';
    const lieuNaissance = etudiant.ville_naissance || '…';
    const cne = etudiant.CNE || etudiant.cne || '-';
    const filiere = inscription?.filiere?.intitule || inscription?.filiere?.libelle || '…';
    const niveau = inscription?.niveau?.intitule || '…';
    const annee = inscription?.anneeUniversitaire?.intitule || '…';
    const today = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

    let logoBase64 = null;
    try {
      logoBase64 = await getBase64FromUrl('/images/logo_sup2i.png');
    } catch (e) {
      console.warn('Logo non trouvé, génération sans logo');
    }

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [60, 60, 60, 60],
      images: logoBase64 ? { logo: logoBase64 } : {},
      content: [
        ...(logoBase64 ? [{ image: 'logo', width: 120, alignment: 'center', margin: [0, 0, 0, 15] }] : []),
        {
          text: 'Attestation de scolarité',
          style: 'title',
          margin: [0, 0, 0, 20]
        },
        {
          stack: [
            {
              text: [
                'Nous soussignés, ',
                { text: 'Mr KROUTI YOUNESS', bold: true },
                ' Directeur pédagogique de l\'',
                { text: 'Ecole Supérieure de l\'Ingénierie et l\'Innovation', bold: true },
                ', autorisée par le Ministère de l\'Enseignement Supérieur, de la Recherche Scientifique et de l\'Innovation sous le n° 216/2004, attestons par la présente que :'
              ],
              alignment: 'justify',
              margin: [0, 0, 0, 15]
            },
            { text: ' ', margin: [0, 0, 0, 5] },
            {
              text: [
                { text: `${civilité} ${nomComplet}`, bold: true },
                ` né(e) le ${dateNaissance} à ${lieuNaissance}`
              ],
              margin: [30, 0, 0, 10]
            },
            { text: ' ', margin: [0, 0, 0, 5] },
            {
              text: [
                'Poursuit ses études à SUP2I en ',
                { text: niveau, bold: true },
                ','
              ],
              alignment: 'justify',
              margin: [0, 0, 0, 5]
            },
            {
              text: [
                'Filière : ',
                { text: filiere, bold: true }
              ],
              margin: [0, 0, 0, 5]
            },
            {
              text: [
                'Au titre de l\'année universitaire ',
                { text: annee, bold: true },
                '.'
              ],
              alignment: 'justify',
              margin: [0, 0, 0, 10]
            },
            { text: ' ', margin: [0, 0, 0, 5] },
            {
              text: 'Cette attestation est délivrée à l\'intéressé(e), pour servir et valoir ce que de droit.',
              alignment: 'justify'
            }
          ]
        },
        { text: ' ', margin: [0, 0, 0, 40] },
        {
          alignment: 'right',
          width: 320,
          stack: [
            { text: `Fait à Casablanca, le ${today}`, margin: [0, 0, 0, 5] },
            { text: 'La Direction Pédagogique', bold: true, margin: [0, 0, 0, 5] }
          ]
        }
      ],
      styles: {
        title: {
          fontSize: 18,
          bold: true,
          alignment: 'center',
          margin: [0, 10, 0, 10]
        }
      },
      footer: {
        stack: [
          { text: 'Supemir sarl - Ecole Supérieure privée Reconnu par l\'Etat - autorisée par le MES sous le n°216/2004', alignment: 'center', fontSize: 9, color: '#666', margin: [0, 10, 0, 0] },
          { text: 'Km 9 susd route n°1 Casa à Rabat - Ain Sebaa – Casablanca (Maroc)', alignment: 'center', fontSize: 9, color: '#666' }
        ]
      }
    };

    pdf.createPdf(docDefinition).download(`Attestation_${cne}_${nomComplet.replace(/\s+/g, '_')}.pdf`);
  } catch (error) {
    console.error('Erreur génération attestation:', error);
    alert('Erreur lors de la génération de l\'attestation');
  }
};

export const exportPresencesToExcel = (presences, etudiants) => {
  const columns = [
    { key: 'etudiant', label: 'Étudiant' },
    { key: 'date', label: 'Date' },
    { key: 'cours', label: 'Cours' },
    { key: 'statut', label: 'Statut' },
    { key: 'heure', label: 'Heure' }
  ];

  const data = presences.map(p => {
    const etudiant = etudiants.find(e => e.id === p.etudiantId || e.etudiant_id === p.etudiantId);
    return {
      etudiant: etudiant ? `${etudiant.nom || etudiant.etudiant_nom} ${etudiant.prenom || etudiant.etudiant_prenom}` : '-',
      date: p.date ? new Date(p.date).toLocaleDateString('fr-FR') : '-',
      cours: p.matiereId || p.cours || '-',
      statut: p.present ? 'Présent' : 'Absent',
      heure: p.heure || p.date ? new Date(p.date).toLocaleTimeString('fr-FR') : '-'
    };
  });

  exportToExcel(data, 'Liste_Presences', columns);
};

